import { ENTITY } from "@/content/entity";
/**
 * One entity graph for the whole site.
 *
 * Search and answer engines resolve entities by `@id`. Emitting Organization,
 * WebSite and per-page nodes as separate top-level JSON-LD blocks made them
 * look like unrelated objects (and duplicated Organization on every route).
 * `siteGraph()` returns a single `@graph` where every node references the
 * others by a stable `@id`, and `seoHead()` emits exactly one ld+json script.
 */
export const SITE_URL = "https://leadxkids.com";

export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const FOUNDER_ID = `${SITE_URL}/#founder`;

export const orgRef = { "@id": ORG_ID } as const;
export const websiteRef = { "@id": WEBSITE_ID } as const;

const FOUNDER_NODE = {
  "@type": "Person",
  "@id": FOUNDER_ID,
  name: "Anvith Murthy",
  jobTitle: "Founder",
  sameAs: ["https://www.linkedin.com/in/anvithmurthy/"],
  worksFor: orgRef,
  knowsAbout: ["Child development", "Life skills education", "Social emotional learning"],
};

const ORGANIZATION_NODE = {
  "@type": ["Organization", "EducationalOrganization"],
  "@id": ORG_ID,
  name: "LeadXkids",
  legalName: "LeadXKids OPC Private Limited",
  alternateName: ["LeadXKids", "Lead X Kids", "LeadX Kids"],
  url: `${SITE_URL}/`,
  logo: {
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#logo`,
    url: `${SITE_URL}/lx-logo-new.png`,
    caption: "LeadXkids",
  },
  image: { "@id": `${SITE_URL}/#logo` },
  description:
    `${ENTITY.brand} is an Indian online platform for ${ENTITY.audience} focused on confidence, public speaking, communication, focus, resilience, emotional skills, friendship skills and personality development. Self-paced, story-based courses.`,
  sameAs: [
    "https://www.instagram.com/leadxkids/",
    "https://www.linkedin.com/company/leadxkids/",
    "https://www.youtube.com/@LeadXKids",
    "https://www.facebook.com/profile.php?id=61579850985077",
    "https://share.google/HgcmoGr9TYWUyuJIN",
  ],
  founder: { "@id": FOUNDER_ID },
  address: {
    "@type": "PostalAddress",
    streetAddress: "36, 4th Main Road",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    postalCode: "560085",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@leadxkids.com",
    areaServed: "IN",
    availableLanguage: ["en", "hi"],
  },
  areaServed: { "@type": "Country", name: "India" },
  knowsAbout: [
    "Child psychology",
    "Confidence building in children",
    "Social emotional learning",
    "Public speaking for kids",
    "Communication skills for children",
    "Resilience and growth mindset",
    "Parenting in India",
  ],
  parentOrganization: {
    "@type": "Organization",
    name: "LeadXKids OPC Private Limited",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Bengaluru",
      addressRegion: "Karnataka",
      addressCountry: "IN",
    },
  },
};

// No site-wide search UI exists, so no SearchAction: claiming one that Google
// cannot exercise is a false signal.
const WEBSITE_NODE = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: "LeadXkids",
  publisher: orgRef,
  inLanguage: "en-IN",
};

export interface GraphPage {
  /** Absolute canonical URL of this page. */
  url: string;
  name: string;
  description?: string;
  image?: string;
  ogType?: string;
  pageType?: string;
}

type Node = Record<string, unknown>;

/**
 * Builds the single `@graph` for a route. `extra` holds page-specific nodes
 * (Article, Course, Product, FAQPage, ItemList, BreadcrumbList …) which are
 * flattened in and stripped of their own `@context`.
 */
export function siteGraph(page: GraphPage | null, extra: Node[] = []): Node {
  const nodes: Node[] = [ORGANIZATION_NODE, FOUNDER_NODE, WEBSITE_NODE];

  if (page) {
    nodes.push({
      "@type": page.pageType ?? "WebPage",
      "@id": page.url,
      url: page.url,
      name: page.name,
      ...(page.description ? { description: page.description } : {}),
      ...(page.image
        ? { primaryImageOfPage: { "@type": "ImageObject", url: page.image } }
        : {}),
      isPartOf: websiteRef,
      about: orgRef,
      inLanguage: "en-IN",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "[data-speakable]"],
      },
    });
  }

  for (const node of extra) {
    if (!node) continue;
    const { "@context": _ctx, ...rest } = node as Node & { "@context"?: unknown };
    // A node may itself be a graph wrapper (e.g. legacy helpers) — flatten it.
    if (Array.isArray((rest as Node)["@graph"])) {
      for (const inner of (rest as Node)["@graph"] as Node[]) {
        const { "@context": _c, ...innerRest } = inner as Node & { "@context"?: unknown };
        nodes.push(innerRest);
      }
      continue;
    }
    nodes.push(rest);
  }

  return { "@context": "https://schema.org", "@graph": nodes };
}
